import {Box, Flex, Text} from '@chakra-ui/react';
import {colors} from 'components/providers';
import {AlertDialog, BreadcrumbLine, FavouriteIndicator} from 'components/shared';
import {useTranslation} from 'hooks';
import {useRouter} from 'next/router';
import {FC, useState} from 'react';
import {ICustomerAddToFavoritesRequest, ICustomerMyFavoritesResponse, IProviderItem} from 'types';
import LinkUI from 'ui/LinkUI';
import NextImageUI from 'ui/NextImage';
import {getDeliveryTimeString} from 'utils/helpers';

import {CloseProviderBanner} from './components';

interface IProps {
  data: IProviderItem;
  isMyFavorite?: ICustomerMyFavoritesResponse;
  onEditFavorite: (data: ICustomerAddToFavoritesRequest) => void;
  tileSize?: string;
  isDesktop?: boolean;
}

export const ProviderTile: FC<IProps> = ({isMyFavorite, onEditFavorite, data, tileSize = '320px', isDesktop}) => {
  const {t} = useTranslation();
  const [isShowAlert, setIsShowAlert] = useState(false);
  const {push} = useRouter();

  const {
    slug,
    avg_delivery_time,
    has_adult_products,
    requires_age_verification,
    showcase_image_thumbnail,
    discount,
    name,
    ratings,
    is_open,
    is_on_pause,
    loyalty_info,
    online_payment,
    fake_id,
    id,
  } = data;

  const deliveryTime = getDeliveryTimeString(avg_delivery_time);

  const forRequest: ICustomerAddToFavoritesRequest = {
    slug,
    data: {
      name: slug,
      id: id,
      has_adult_products,
      requires_age_verification,
    },
  };

  const providerRating = parseFloat(`${ratings?.average}`);
  const rating = providerRating > 0 ? providerRating.toFixed(2) : '';

  const pauseTitle = !is_open ? `${t('CLOSE_PROVIDER')}` : t('TEMP_CLOSE_PROVIDER');
  const next_opening_hours = data.next_opening_hours;

  const toggleAlertDialog = () => {
    setIsShowAlert((prev) => !prev);
  };

  const qyery = fake_id ? {fake_id: fake_id} : undefined;

  return (
    <LinkUI
      href={{
        pathname: `/restaurant/${slug}`,
        query: {...qyery},
      }}
    >
      <Box display="block" mb="15px" w={tileSize}>
        <Box position="relative" mb="15px" borderRadius="16px" w={tileSize}>
          {(!is_open || is_on_pause || next_opening_hours) && (
            <CloseProviderBanner>{next_opening_hours ? next_opening_hours : pauseTitle}</CloseProviderBanner>
          )}
          {has_adult_products && <CloseProviderBanner>{t('FOR_ADULT')}</CloseProviderBanner>}
          <Box w={tileSize} h="180px">
            <Box
              w={tileSize}
              h="180px"
              transition={['none', 'transform .3s ease']}
              borderRadius="16px"
              overflow="hidden"
              position="relative"
              _hover={{
                transform: isDesktop ? 'scale3d(1.1,1.1,1)' : 'none',
                transition: '.3s ease',
              }}
            >
              <NextImageUI
                src={showcase_image_thumbnail}
                alt={slug}
                fallbacksrc="/food_pattern.webp"
                fill
                sizes="(max-width: 380px) calc(100vw - 30px), 320px"
                style={{
                  borderRadius: '16px',
                  objectFit: 'cover',
                }}
              />
            </Box>
          </Box>

          <FavouriteIndicator
            isFavorite={isMyFavorite && isMyFavorite.slug === data.slug}
            data={forRequest}
            onEditFavorite={onEditFavorite}
          />
        </Box>
        <Flex justify="space-between" alignItems="center" lineHeight="17px" mb="1px">
          <Text fontSize={['14px', '16px']} fontWeight={700} noOfLines={1}>
            {name}
          </Text>
        </Flex>
        <BreadcrumbLine ratingsCount={rating} deliveryTime={deliveryTime} maxW={tileSize} />
        <Flex alignItems="center" mt="6px" justifyContent="space-between">
          {!online_payment && (
            <Box color="rgb(5, 150, 250)" bg="rgba(5, 150, 250, 0.1)" p="2px" px="10px" borderRadius="md">
              <Text ml={discount ? '5px' : '0px'} fontSize="10px" fontWeight="semibold">
                {t('onlyCashPayment')}
              </Text>
            </Box>
          )}
          {discount && (
            <Box bg="rgba(250, 5, 111, 0.1)" p="2px" px="10px" borderRadius="md">
              <Text ml={discount ? '5px' : '0px'} fontSize="10px" color={colors.pink} fontWeight="semibold">
                {discount}% {t('DISCOUNT')}
              </Text>
            </Box>
          )}
          {loyalty_info && (
            <Box bg="rgba(250, 5, 111, 0.1)" p="2px" borderRadius="md" px="10px">
              <Text ml={discount ? '5px' : '0px'} fontSize="10px" color={colors.pink} fontWeight="semibold">
                {loyalty_info}
              </Text>
            </Box>
          )}
        </Flex>
      </Box>
      {isShowAlert && (
        <AlertDialog
          title={t('YOU_HAVE_18_YEARS')}
          isOpen={isShowAlert}
          onClose={toggleAlertDialog}
          onConfirm={() => push(`/restaurant/${slug}`)}
          successButtonValue={t('yes')}
          cancelButtonValue={t('no')}
        />
      )}
    </LinkUI>
  );
};
